import useLeadsStore from '../../../store/LeadsStore';
import { LeadsModalModeEnum } from '../../../api/models/Enums';
import i18next from 'i18next';
import Icon from '../../Icon/Icon';
import { useRef, useEffect, useCallback, forwardRef, InputHTMLAttributes, MutableRefObject } from 'react';
import ColumnFilterDoubleInput from '../../VehicleTable/components/filters/ColumnFilterDoubleInput';
import filterFn from '../../VehicleTable/customFilterFn';
import ColumnFilterInput from '../../VehicleTable/components/filters/ColumnFilterInput';
import useModalStore from '../../../store/ModalStore';
import { formatDate } from '../../../utils';

import toaster from '../../../utils/toaster';
import { useLeadDetailHref } from '../hooks/useLeadDetailHref';

type IndeterminateCheckboxProps = InputHTMLAttributes<HTMLInputElement> & { indeterminate?: boolean }

const IndeterminateCheckbox = forwardRef<HTMLInputElement, IndeterminateCheckboxProps>(
  ({ indeterminate, ...rest }: any, ref: any) => {
    const defaultRef: MutableRefObject<HTMLDivElement | null> = useRef(null)
    const resolvedRef = ref || defaultRef

    useEffect(() => {
      if (!resolvedRef.current) return;
      resolvedRef.current.indeterminate = indeterminate
    }, [resolvedRef, indeterminate])


    return (
      <input
        type="checkbox"
        ref={resolvedRef}
        {...rest}
        style={{ width: "20px", height: "20px", cursor: "pointer" }}
      />
    )
  }
)

const MAX_CHECKED_LEADS = 30;


const useColumnsLeadsList = () => {
  const { toggleCheckedLead, setCheckedLeads, setActiveLead } = useLeadsStore();
  const { openModal } = useModalStore();
  const leadDetailHref = useLeadDetailHref();

  const toggleAllChecked = useCallback(({ toggleAllPageRowsSelected, toggleAllRowsSelected, isAllPageRowsSelected, page }) => {

    const leads = (page || []).map((pageItem) => pageItem.original).filter(Boolean);

    toggleAllRowsSelected(false)
    toggleAllPageRowsSelected()
    if (leads.length > MAX_CHECKED_LEADS) {
      toaster.error(i18next.t('cannot_select_more_then_30_leads'));
      return;
    }
    setCheckedLeads(isAllPageRowsSelected ? [] : leads.map((lead) => lead.Id))
  }, [setCheckedLeads]);

  return [
    {
      width: 40,
      accessor: 'checkboxes',
      id: 'checkboxes',
      Header: ({ getToggleAllPageRowsSelectedProps, ...rest }) => (
        <div className="flex items-center">
          <IndeterminateCheckbox
            {...getToggleAllPageRowsSelectedProps()}
            onChange={() => toggleAllChecked(rest as any)}
          />
        </div>
      ),
      disableSortBy: true,
      Cell: ({ row }) => (
        <div className="flex items-center gap-2 cursor-pointer">
          <IndeterminateCheckbox
            {...row.getToggleRowSelectedProps()}
            onChange={() => {
              row.toggleRowSelected();
              toggleCheckedLead(row.original);
            }}
          />
        </div>
      ),
    },
    {
      accessor: 'CreatedDate',
      id: 'CreatedDate',
      Header: i18next.t('date_creation'),
      inputType: 'date',
      SecondaryFilter: ColumnFilterDoubleInput,
      filter: filterFn,
      width: 200,
      Cell: ({ row }) => {
        const rawDate = row?.original?.CreatedDate;
        const formatted = formatDate(rawDate, 'it-IT', false);
        return formatted === 'Invalid Date' ? rawDate : formatted;
      },
    },
    {
      accessor: 'LeadSource',
      id: 'LeadSource',
      Header: i18next.t('source'),
      width: 180,
    },
    {
      accessor: 'FirstName',
      id: 'FirstName',
      Header: i18next.t('first_name'),
      PrimaryFilter: ColumnFilterInput,
      width: 180,
      inputType: 'search',
    },
    {
      accessor: 'LastName',
      id: 'LastName',
      Header: i18next.t('last_name'),
      PrimaryFilter: ColumnFilterInput,
      width: 180,
      inputType: 'search',
    },
    {
      accessor: 'Company',
      id: 'Company',
      Header: i18next.t('business_name'),
      PrimaryFilter: ColumnFilterInput,
      width: 180,
      inputType: 'search',
    },
    {
      accessor: 'Marca__c',
      id: 'Marca__c',
      Header: i18next.t('brand'),
      SecondaryFilter: ColumnFilterInput,
      width: 200,
      inputType: 'search',
    },
    {
      accessor: 'Modello__c',
      id: 'Modello__c',
      Header: i18next.t('model'),
      SecondaryFilter: ColumnFilterInput,
      width: 250,
      inputType: 'search',
    },
    {
      accessor: 'City',
      id: 'City',
      Header: i18next.t('city'),
      SecondaryFilter: ColumnFilterInput,
      width: 150,
      inputType: 'search',
    },
    {
      accessor: 'Filiale_Dealer_Titolare__c',
      id: 'Filiale_Dealer_Titolare__c',
      Header: i18next.t('dealer'),
      SecondaryFilter: ColumnFilterInput,
      width: 150,
      inputType: 'search',
    },
    {
      accessor: 'SalesUser',
      id: 'SalesUser',
      Header: i18next.t('sales_user'),
      filter: filterFn,
      width: 300,
    },
    {
      accessor: 'Email',
      id: 'Email',
      Header: i18next.t('email'),
      SecondaryFilter: ColumnFilterInput,
      width: 250,
      inputType: 'search',
    },
    {
      accessor: 'Phone',
      id: 'Phone',
      Header: i18next.t('mobile_phone'),
      SecondaryFilter: ColumnFilterInput,
      width: 220,
      inputType: 'search',
    },
    {
      accessor: 'Codice_Fiscale__c',
      id: 'Codice_Fiscale__c',
      Header: i18next.t('fiscal_code'),
      PrimaryFilter: ColumnFilterInput,
      width: 300,
      inputType: 'search',
    },
    {
      accessor: 'Partita_Iva__c',
      id: 'Partita_Iva__c',
      Header: i18next.t('business_fiscal_code'),
      PrimaryFilter: ColumnFilterInput,
      width: 180,
      inputType: 'search',
    },
    {
      width: 60,
      accessor: 'actions',
      id: 'actions',
      Header: '',
      disableSortBy: true,
      headerClassName: 'column-sticky',
      className: 'column-sticky text-end',
      Cell: ({ row }) => {
        return (
          <a
            className="btn"
            href={leadDetailHref(row.original.Id)}
            aria-label={i18next.t('lead_details')}
            onClick={(e) => {
              if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
              e.preventDefault();
              openModal(LeadsModalModeEnum.DETAILS);
              setActiveLead(row.original);
            }}
          >
            <Icon style={{ width: '1.2rem', height: '1.2rem' }} name="arrow-right" />
          </a>
        );
      },
    },
  ];
};

export default useColumnsLeadsList;
